import { useIsMobile } from '@bbl-digital/snorre'
import { Button, IconChevronDown, Text } from '@bbl-digital/snorre-v2'
import { FC, useMemo } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { PortalAvailable } from 'shared/constants/portalAvailable'
import { IMenu, IMenuItem } from 'shared/models/Menu'
import { cn } from 'shared/utils/cn'
import { isArendal } from 'shared/utils/theme'

import { isActiveParent } from 'shared/utils/url'

export type NewMenuItemProps = {
  item: IMenu
  icon: React.ReactNode
  small?: boolean
  onClick?: () => void
  active?: boolean
  ariaCurrent?:
    | 'page'
    | 'step'
    | 'location'
    | 'date'
    | 'time'
    | 'true'
    | 'false'
  isExpanded?: boolean
  setCurrentExpandedMenuUrl?: (menu: string) => void
}

export const MenuItem: FC<NewMenuItemProps> = ({
  item,
  icon,
  small,
  onClick,
  active,
  ariaCurrent,
  isExpanded,
  setCurrentExpandedMenuUrl,
}) => {
  const { displayName, url, menuItems } = item
  const isMobile = useIsMobile()
  const location = useLocation()

  const linkClasses = cn(
    'flex flex-1 items-center gap-1 min-h-full w-full box-border py-[10px] px-2 rounded-[10px] hover:bg-surface-portalen-aside-highlight cursor-pointer box-border min-h-6 min-w-6  items-center flex-nowrap text-nowrap ',
    small ? 'px-1 py-1 items-center justify-center' : 'justify-between',
    active && 'bg-surface-portalen-aside-highlight'
  )

  const handleExpand = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.preventDefault()
    e.stopPropagation()
    setCurrentExpandedMenuUrl?.(isExpanded ? null : url)
  }

  const handleClick = () => {
    if (isMobile && menuItems?.length === 0) {
      return
    }
    setCurrentExpandedMenuUrl?.(isExpanded ? null : url)
  }

  const mainUrl = () => {
    if (menuItems?.length === 0) return url
    if (menuItems?.length > 0) return menuItems[0].url
    return url
  }

  const shouldNavigateOnClick = useMemo(() => {
    const isOnHome = location.pathname === '/'
    // Handle mobile navigation
    if (isMobile) {
      if (url !== '/') return false
      return !isOnHome
    }

    // Handle desktop navigation
    if (!isOnHome && url === '/') return true
    if (location.pathname.includes(url)) return false
    return true
  }, [location.pathname, url, isMobile])

  const MainMenuItemContent = (
    <>
      <div className="flex items-center gap-[10px]">
        <div className="[&>svg]:!h-3 [&>svg]:w-3 min-h-3 min-w-3 flex items-center justify-center">
          {icon}
        </div>
        {!small && (
          <Text
            className={cn(
              'font-semibold text-text-invert text-ellipsis text-nowrap',
              isArendal() && 'text-surface-portalen-aside-icon-active'
            )}
          >
            {displayName}
          </Text>
        )}
      </div>
      {!small && menuItems?.length > 0 && (
        <div
          role="button"
          onClick={handleExpand}
          className={isExpanded && 'rotate-180'}
          aria-expanded={isExpanded}
          aria-label={`${displayName} ${isExpanded ? 'lukk' : 'åpne'}`}
          tabIndex={0}
        >
          <IconChevronDown
            className={cn(
              'text-surface-portalen-aside-icon',
              active && 'text-surface-portalen-aside-icon-active'
            )}
          />
        </div>
      )}
    </>
  )

  return (
    <li role="menuitem">
      {shouldNavigateOnClick ? (
        <Link
          to={mainUrl()}
          className={linkClasses}
          onClick={handleClick}
          aria-haspopup={false}
          aria-current={ariaCurrent}
          aria-label={displayName}
          title={displayName}
        >
          {MainMenuItemContent}
        </Link>
      ) : (
        <Button
          variant="nostyle"
          className={linkClasses}
          onClick={handleClick}
          tabIndex={-1}
        >
          {MainMenuItemContent}
        </Button>
      )}
      {!small && isExpanded && menuItems?.length > 0 && (
        <SubMenuList
          menuItems={menuItems}
          onClick={() => onClick?.()}
          location={location}
        />
      )}
    </li>
  )
}
const SubMenuList: FC<{
  menuItems: IMenuItem[]
  onClick: () => void
  location: ReturnType<typeof useLocation>
}> = ({ menuItems, onClick, location }) => {
  const linkClasses = cn(
    'flex hover:bg-surface-portalen-aside-highlight py-1 px-[12px] rounded-md w-full box-border group'
  )

  //TODO: Dette må bli fjernet på et senere tidspunkt!!
  const hasGeneralforsamling2 = menuItems.some(
    (item) => item.portalTilgangsobjektId === PortalAvailable.AnnualMeeting2
  )

  const filteredMenuItems = hasGeneralforsamling2
    ? menuItems.filter(
        (item) => item.portalTilgangsobjektId !== PortalAvailable.AnnualMeeting
      )
    : menuItems
  //TODO: Koden over må fjernes på et senere tidspunkt!!

  return (
    <ul className="flex flex-col mt-[4px] mx-1 ml-2" role="menu">
      {filteredMenuItems?.map((item) => (
        <li
          key={item.url}
          role="menuitem"
          className={cn(
            'border-l-[2px] border-surface-portalen-aside-highlight pl-1',
            isActiveParent(item.url, location.pathname) &&
              'border-surface-portalen-aside-icon-active'
          )}
        >
          <Link
            to={item.url}
            key={item.url}
            onClick={onClick}
            aria-label={item.displayName}
            className={cn(linkClasses)}
            title={item.displayName}
          >
            <Text
              className={cn(
                'text-text-invert text-opacity-80 text-ellipsis text-nowrap group-hover:text-opacity-100',
                isActiveParent(item.url, location.pathname) &&
                  'text-surface-portalen-aside-icon-active font-bold text-opacity-100',
                isArendal() && 'text-surface-portalen-aside-icon-active'
              )}
            >
              {item.displayName}
            </Text>
          </Link>
        </li>
      ))}
    </ul>
  )
}
